'use client'

import { motion } from 'framer-motion'
import { Check, Layers } from 'lucide-react'

interface Pillar {
  number: string
  title: string
  tagline: string
  points: string[]
}

interface AutomotivePillarsSectionProps {
  eyebrow?: string
  heading: string
  intro?: string
  pillars: Pillar[]
}

export function AutomotivePillarsSection({ eyebrow, heading, intro, pillars }: AutomotivePillarsSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-muted/20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
            >
              <Layers className="h-4 w-4 text-primary" />
              <span className="eyebrow">{eyebrow}</span>
            </motion.div>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
          >
            {heading}
          </motion.h2>
          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="reveal reveal-delay-2 text-lg text-muted-foreground"
            >
              {intro}
            </motion.p>
          )}
        </div>

        {/* Pillar grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="reveal group relative rounded-3xl border border-border/50 bg-white dark:bg-zinc-900 p-7 transition-all duration-300 lift-hover"
              style={{ transitionDelay: `${(index % 4) * 80}ms` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span aria-hidden="true" className="text-3xl font-black text-primary/70 group-hover:text-primary/90 transition-colors">
                  {pillar.number}
                </span>
                <h3 className="text-lg font-bold text-foreground">{pillar.title}</h3>
              </div>
              <p className="text-sm font-medium text-primary/90 mb-5">{pillar.tagline}</p>
              <ul className="space-y-2.5">
                {pillar.points.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
